import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';
import isNumber from 'lodash/isNumber';
import { generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import RxSocket from '@Services/RxSocket';
import SocketBroker from '@Services/SocketBroker';
import Breadcrumb from '@Services/Breadcrumb';
import { RxSocketUrls } from '@Constants/CommonConstants';
import BatchExecutionDetailsStorageManager from '@Services/BatchExecutionDetailsStorageManager';

export class BatchExecutionDetailsService {
  _searches = {};

  _generalErrors = null;

  _unregisterErrors = noop;

  _unregisterSearch = noop;

  _pushItemInSearch = (item) => {
    const currentSearch = this._searches[item.searchId];
    const dbId = item.databaseId;
    if (!currentSearch) {
      return;
    }

    if (!currentSearch.bases[dbId]) {
      currentSearch.bases[dbId] = [];
    }

    currentSearch.bases[dbId][item.itemNumber] = item;
  };

  _updateWithGeneralErrors = (items) => {
    if (!this._generalErrors) {
      return;
    }
    this._generalErrors.forEach((error, index) => {
      if (isUndefined(items[index])) {
        // eslint-disable-next-line no-param-reassign
        items[index] = { itemNumber: +index, registryIds: error };
      }
    });
  };

  _setupWebSockets = (queryNo, fileName, callback) => {
    this._unregisterErrors = SocketBroker.observe(RxSocketUrls.ChannelsUrls.QueueErrors, this._handleError);
    this._unregisterSearch = SocketBroker.observe(
      RxSocketUrls.ChannelsUrls.SearchInfo,
      this._handleSearch.bind(null, queryNo, fileName, callback)
    );
  };

  unregisterSockets = () => {
    this._unregisterErrors();
    this._unregisterErrors = noop;

    this._unregisterSearch();
    this._unregisterSearch = noop;
  };

  _handleError = (error) => {
    Logger.error(error);
    this.unregisterSockets();
  };

  // eslint-disable-next-line consistent-return
  _handleSearch = (queryNo, fileName, callback, data) => {
    if (get(data, 'payload.status', null) === 'OK') {
      this.unregisterSockets();
      if (callback) {
        callback();
      }

      const result = data.payload;
      const params = {
        context: result.context,
        databaseId: result.databaseId,
        queryId: result.queryId,
        pageNumber: 1,
        resultName: result.resultName,
        searchId: result.searchId,
        description: `Search from query No ${queryNo} of file "${fileName}"`,
        comment: `Search from query No ${queryNo} of file "${fileName}"`,
        hitSetKey: result.hitSetKey,
        isFromAdvancedSearch: 1,
        historyType: 'batchSingleResult',
      };

      return Breadcrumb.createBreadcrumb(params, result.resultSize)
        .then(() => {
          const pathName = generateResultsPath(params);
          history.push({
            pathname: pathName,
            state: params,
          });
        })
        .catch(Logger.reject);
    }
  };

  /**
   * @name clear
   * @description clear cashed search details
   */
  clear = () => {
    this._searches = {};
    this._generalErrors = null;
  };

  /**
   * @name getById
   * @param searchId
   * @param dbId
   * @description Return details for corresponding database and search
   */
  getById = (searchId, dbId) => {
    // get from IndexedDB
    const storage = BatchExecutionDetailsStorageManager.getInstance();
    return storage.get(searchId).then((search) => {
      if (!search) {
        Logger.debug('BatchExecutionDetailsService - search is missing', searchId, search);
        return null;
      }

      const items = get(search, `bases.${dbId}`, []);

      return {
        filename: search.filename,
        options: search.options,
        context: search.context,
        queries: search.queries,
        items,
      };
    });
  };

  /**
   * @name addItem
   * @param item
   * @description Add item into search related to items db and searchId
   */
  addItem = (item) => {
    if (isNumber(item.itemNumber)) {
      this._pushItemInSearch(item);
    }
  };

  /**
   * @name addErrorItem
   * @param item
   * @description Add error item into linked search
   */
  addErrorItem = (item) => {
    if (isNumber(item.itemNumber)) {
      // eslint-disable-next-line no-param-reassign
      item.registryIds = item.executionMessage;
      this._pushItemInSearch(item);
    }
  };

  /**
   * @name getDetailsForBreadcrumb
   * @param crumb
   * @description Returns details for corresponding breadcrumb
   */
  getDetailsForBreadcrumb = (crumb) => {
    return this.getById(crumb.searchId, crumb.databaseId);
  };

  /**
   * @name createSearch
   * @param searchId
   * @param context
   * @param file - search file
   * @param options - search options
   * @description Creates new cashed search
   */
  createSearch = (searchId, context, file, options) => {
    this._generalErrors = file.errorMessages;

    this._searches[searchId] = {
      searchId,
      context,
      filename: file.filename,
      queries: file.queries,
      options,
      bases: {},
    };
  };

  /**
   * @name commit
   * @param searchId
   * @param databaseId
   * @description Save search for selected searchId and database into Local Storage
   */
  commit = async (searchId, databaseId) => {
    if (this._searches && this._searches[searchId]) {
      this._updateWithGeneralErrors(this._searches[searchId].bases[databaseId]);

      // update in IndexedDB
      const storage = BatchExecutionDetailsStorageManager.getInstance();
      await storage.set(searchId, this._searches[searchId]);
    }
  };

  /**
   * @name searchSingleQuery
   * @param payload
   * @param queryNo - used into breadcrumb comment
   * @param fileName - used into breadcrumb comment
   * @description Send and handle corresponding search
   */
  searchSingleQuery = (payload, queryNo, fileName, callback) => {
    this.unregisterSockets();
    this._setupWebSockets(queryNo, fileName, callback);
    RxSocket.send(RxSocketUrls.AdvancedQuery, payload);
  };
}

const BatchExecutionDetails = new BatchExecutionDetailsService();
export default BatchExecutionDetails;
